/**
 * Punto de extensión de los formularios de alta y edición, LLENO por Jupiter:
 * la carga del DNI del autor y, en la edición, su estado (AuthorStatusPanel).
 *
 * SADAIC exige la foto del DNI de cada autor. Hasta ahora la única puerta era
 * el link temporal que se le manda por mail; desde acá la puede cargar quien
 * administra el sitio, si la tiene a mano. Es el MISMO modal, con la misma
 * validación local y la misma entrega — lo único que cambia es la credencial
 * con la que viaja: sesión en vez del vid+sig del mail.
 *
 * La acción que abre la carga no está acá: es un ícono junto al CUIT/DNI, en
 * el campo propio (AuthorComposerTaxIdCustomField, KAN-463). Acá queda el
 * diálogo, porque solo este asiento recibe `onPatch` y `options`; el campo lo
 * pide con `pedirCargaDeDni` y este lo atiende con `useAtenderCargaDeDni`, solo
 * para su formulario (ver pedidoDeCargaDeDni.ts).
 *
 * En el ALTA la acción también aparece, pero solo sirve para leer el documento y
 * completar los campos: el autor todavía no existe, así que no hay a quién
 * atribuirle la foto y el envío a SADAIC queda deshabilitado hasta guardar.
 *
 * Contrato de props, que lo define el generador y no conoce este dominio:
 *  - entity:   el nombre de la entidad ("AuthorComposer", "Work", ...), para
 *              que el proyecto decida en qué formularios aparece algo.
 *  - mode:     "create" o "update". Importa: en un alta el registro TODAVÍA NO
 *              EXISTE, así que lo que dependa de su id no se puede hacer ahí.
 *  - recordId: el id del registro, solo en "update".
 *  - values:   los valores actuales del formulario, por nombre de campo.
 *  - options:  las opciones de cada campo de referencia editable, por nombre.
 *              Hacen falta para poder completarlo: un campo de referencia
 *              guarda la FILA elegida, no un texto.
 *  - onPatch:  escribe un campo. El formulario es el único que sabe cómo se
 *              setea cada uno de los suyos —un escalar y una referencia no se
 *              setean igual—, así que expone un despachador en vez de que el
 *              proyecto tenga que conocer sus entrañas. Los campos de solo
 *              lectura no están: pedirlos no hace nada.
 *
 * El archivo llega como STUB desde el generador y este contenido es de Jupiter:
 * el DNI argentino y SADAIC no son dominio del generador. El stub allá es
 * estable a propósito, así el merge base→develop preserva esta versión; lo
 * único que cambia de un lado es este encabezado, que describe el contrato.
 */

import React, { useRef, useState } from "react";
import { observer } from "mobx-react";
import { Box } from "@mui/material";
import { useRootStore } from "../../store/contexts";
import { DniUploadDialog } from "../pages/dniUpload/DniUploadDialog";
import { buscarOpcion } from "../pages/dniUpload/referencias";
import { AuthorStatusPanel } from "../../entities/authorComposer/components/common/AuthorStatusPanel";
import { filaDeNacionalidadDelFormulario } from "../pages/dniUpload/nacionalidad";
import { diaDeCalendario, isCalendarDate } from "../calendarDate";
import { useAtenderCargaDeDni } from "../pages/dniUpload/pedidoDeCargaDeDni";

export interface OpcionDeReferencia {
  id: string;
  displayId: string;
}

export interface FormExtrasProps {
  entity: string;
  mode: "create" | "update";
  recordId?: string;
  values: Record<string, any>;
  options?: Record<string, OpcionDeReferencia[]>;
  onPatch?: (campo: string, valor: any) => void;
}

const texto = (campo: any): string => (campo == null ? "" : String(campo));

const FormExtras: React.FC<FormExtrasProps> = observer(
  ({ entity, mode, recordId, values, options, onPatch }) => {
    const { referenceCacheStore } = useRootStore();
    const [abierto, setAbierto] = useState(false);
    // El ícono «Carga de DNI» del CUIT/DNI de este formulario abre el diálogo.
    const marca = useRef<HTMLDivElement>(null);
    useAtenderCargaDeDni(marca, () => setAbierto(true));

    // Solo los autores tienen DNI que mandarle a SADAIC.
    if (entity !== "AuthorComposer") return null;
    // El autor que se edita; en el alta todavía no existe, aunque llegue un recordId.
    const autorEditado = mode === "update" && recordId ? recordId : undefined;

    const author = {
      firstName: texto(values.firstName),
      lastName: texto(values.lastName),
      taxId: texto(values.taxId),
      taxIdType: texto(values.taxIdType),
      // El formulario tiene la fecha como el día, «AAAA-MM-DD», que es como la
      // compara el matcher contra el MRZ; sin fecha, va vacía.
      dateOfBirth: diaDeCalendario(values.dateOfBirth) ?? "",
      gender: texto(values.gender),
    };

    /**
     * La nacionalidad va por el código del país, no por el nombre: las opciones
     * son gentilicios y «argelina» va antes que «argentina», así que ARG elegía
     * Argelia. El código está en las filas crudas de la caché de países (ver
     * `filaDeNacionalidadDelFormulario`). Si la caché no cargó, esta vez va por
     * nombre y se pide cargarla para la próxima.
     */
    const filaDeLaNacionalidad = (opciones: OpcionDeReferencia[], codigo: unknown) => {
      const paises = referenceCacheStore.getRawData("Country");
      if (paises.length === 0) void referenceCacheStore.ensureLoaded("Country");
      return filaDeNacionalidadDelFormulario(opciones, paises, codigo);
    };

    /**
     * Vuelca al formulario lo leído del documento.
     *
     * Los escalares van directo. Las referencias NO: el formulario guarda la
     * fila elegida, no un texto, así que hay que encontrarla en el listado. El
     * MRZ trae la nacionalidad en tres letras —ARG— y el catálogo la nombra
     * entera —Argentina—: esa se busca por código de país; el resto, por id,
     * nombre o primeras letras (ver `buscarOpcion`).
     *
     * Lo que no se encuentra no se escribe: es preferible dejar el campo como
     * estaba a llenarlo con la fila equivocada.
     */
    const volcar = (valores: Record<string, any>) => {
      if (!onPatch) return;
      for (const [campo, valor] of Object.entries(valores)) {
        const opciones = options?.[campo];
        if (!opciones) {
          if (campo === "dateOfBirth") {
            // El formulario guarda el día como «AAAA-MM-DD», igual que lo da el
            // documento. Lo que no es un día que existe (incompleto, el 30 de
            // febrero, otro formato) no se escribe.
            if (isCalendarDate(valor)) onPatch(campo, valor);
            continue;
          }
          onPatch(campo, valor);
          continue;
        }
        const fila =
          campo === "nationality"
            ? filaDeLaNacionalidad(opciones, valor)
            : // El id de la fila, su nombre o un código del documento.
              buscarOpcion(opciones, valor);
        if (fila) onPatch(campo, fila);
      }
    };

    return (
      // La caja queda también en el alta, sin nada a la vista: es la marca con la
      // que se encuentra el formulario (ver useAtenderCargaDeDni).
      <Box ref={marca} sx={{ mb: autorEditado ? 2 : 0 }}>
        {/* En la edición, lo que el autor validó con su DNI y lo que le falta
            para entrar en una documentación (KAN-450). */}
        {autorEditado && <AuthorStatusPanel recordId={autorEditado} />}

        <DniUploadDialog
          open={abierto}
          lookups={options}
          onExtracted={onPatch ? (valores) => volcar(valores) : undefined}
          onClose={() => setAbierto(false)}
          // En el alta no hay registro al que atribuirle la foto: el modal lo
          // sabe y deshabilita el envío, pero deja leer el documento.
          destino={autorEditado ? { tipo: "admin", authorComposerId: autorEditado } : null}
          author={author}
          domicilioCargado={!!texto(values.streetAndNumber).trim()}
          onUploaded={() => undefined}
        />
      </Box>
    );
  }
);

export default FormExtras;
